import React from 'react';
import { FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { getSiteConfig, isCdSite } from '../../utils/siteConfig';

type IconComponent = React.ComponentType<{ className?: string }>;
const XIcon = FiX as IconComponent;

const WHATSAPP_CONTACTS = [
  {
    id: 'kinshasa',
    cityKey: 'whatsApp.contacts.kinshasa',
    phone: '+243 819 935 053',
    waNumber: '243819935053',
  },
  {
    id: 'lubumbashi',
    cityKey: 'whatsApp.contacts.lubumbashi',
    phone: '+243 841 364 201',
    waNumber: '243841364201',
  },
] as const;

const toWhatsAppNumber = (phone: string) => phone.replace(/\D/g, '');

const WhatsAppButton: React.FC = () => {
  const { t } = useTranslation();
  const isCdWebsite = isCdSite();
  const siteConfig = getSiteConfig();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const message = t('whatsApp.message');
  const defaultWhatsAppNumber = toWhatsAppNumber(siteConfig.phoneNumbers[0] || '+243841364201');

  React.useEffect(() => {
    if (!isModalOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsModalOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isModalOpen]);

  const openWhatsApp = (waNumber: string) => {
    const whatsappUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    setIsModalOpen(false);
  };

  const handleButtonClick = () => {
    if (isCdWebsite) {
      setIsModalOpen(true);
      return;
    }

    openWhatsApp(defaultWhatsAppNumber);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleButtonClick}
        aria-label={t('whatsApp.tooltip')}
        className="group fixed bottom-5 right-4 z-[1000] flex h-12 w-12 items-center justify-center rounded-2xl border border-emerald-100 bg-white text-[#14A44D] shadow-[0_10px_24px_rgba(20,164,77,0.2)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-emerald-50 hover:shadow-[0_14px_28px_rgba(20,164,77,0.28)] sm:bottom-6 sm:right-6 sm:h-14 sm:w-14"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-6 w-6 sm:h-7 sm:w-7">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
        </svg>
        <span className="pointer-events-none absolute right-14 hidden whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1 text-xs font-medium text-white opacity-0 transition-opacity duration-200 group-hover:block group-hover:opacity-100 sm:right-16">
          {t('whatsApp.tooltip')}
        </span>
      </button>

      {isCdWebsite && isModalOpen && (
        <div
          className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-900/45 px-4"
          onClick={() => setIsModalOpen(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="whatsapp-modal-title"
          >
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <h3 id="whatsapp-modal-title" className="text-lg font-bold text-slate-800">
                {t('whatsApp.modal.title')}
              </h3>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label={t('whatsApp.modal.closeAria')}
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3 p-5">
              {WHATSAPP_CONTACTS.map((contact) => (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => openWhatsApp(contact.waNumber)}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-left transition hover:border-[#14A44D] hover:bg-emerald-50 focus:border-[#14A44D] focus:outline-none focus:ring-2 focus:ring-emerald-100"
                >
                  <span className="block text-base font-semibold text-slate-800">{t(contact.cityKey)}</span>
                  <span className="mt-1 block text-sm text-slate-600">{contact.phone}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WhatsAppButton;